import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import type { ScaleBand } from 'd3-scale';
import { useRef } from 'react';

import { axisContainerStyle } from './AxisArea.sty.js';
import { XTickWithTruncation } from './XTickWithTruncation.js';
import { XYChartPanningControls } from './XYChartPanningControls.js';
import { AxisBottom } from '../../../core/components/axes/AxisBottom.js';
import { DivAxisWrapper } from '../../../core/components/axes/components/DivAxisWrapper.js';
import { axisFontProps } from '../../../core/components/axes/constants.js';
import { getXTickMaxSize } from '../../../core/components/axes/utils/calculate-axis-size.js';
import {
  getTickLabelPropsForCategoryAxis,
  getTickLabelPropsForValueAxis,
} from '../../../core/components/axes/utils/get-tick-label-props.js';
import { isTickLabelSpaceAvailable } from '../../../core/components/axes/utils/is-tick-label-visible.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { KEYBOARD_HANDLERS } from '../../../core/components/zoom/constants.js';
import { cursorStyle } from '../../../core/components/zoom/ZoomCursors.sty.js';
import { useAxesTicks } from '../../hooks/useAxesTicks.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useZoomState } from '../../store/selectors.js';
import { isCategorical } from '../../types/guards.js';
import { AREAS } from '../../utils/grid-templates.js';
import { useApplyPanDelta } from '../overlay/hooks/useApplyPanDelta.js';

export function XYChartBottomAxisArea() {
  const axisRef = useRef<HTMLDivElement>(null);

  const { bottomTicks } = useAxesTicks();
  const { bottom: bottomConfig } = useVisibleAxesConfig();
  const { bottom: bottomScale } = useVisibleAxesScales();
  const { zoomOutEnabled } = useZoomState();
  const {
    axesMetadata: { isPanEnabled },
  } = useXYChartData();

  const canPan = isPanEnabled && zoomOutEnabled;

  const tickFormat = (_: unknown, index: number) =>
    bottomTicks.formattedTicks[index];

  const isCategoricalAxis = bottomConfig?.type
    ? isCategorical(bottomConfig.type)
    : false;
  const tickLabelProps = isCategoricalAxis
    ? getTickLabelPropsForCategoryAxis('bottom', bottomConfig?.tickLabelLayout)
    : getTickLabelPropsForValueAxis('bottom', bottomConfig?.tickLabelLayout);

  const areCategoricalTicksNotFittable =
    bottomScale &&
    isCategoricalAxis &&
    !isTickLabelSpaceAvailable((bottomScale as ScaleBand<string>).step());

  const handlePan = useApplyPanDelta();

  useGesture(
    {
      onDrag: (e) =>
        KEYBOARD_HANDLERS.includes(e.type) ? null : handlePan(-e.delta[0]),
    },
    {
      target: axisRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true, pointer: { buttons: [1, 4] } },
      enabled: canPan,
    },
  );

  const hideAxis =
    bottomConfig === undefined ||
    areCategoricalTicksNotFittable ||
    bottomScale === undefined;

  const isTickRotated = bottomConfig?.tickLabelLayout === 'vertical';

  return hideAxis ? null : (
    <ChartGrid.GridArea gridArea={AREAS.bottomAxis}>
      {({ availableWidth, availableHeight }) => (
        <div
          className={clsx(
            cursorStyle({ isPanEnabled: canPan }),
            axisContainerStyle,
          )}
          ref={axisRef}
        >
          {canPan && (
            <XYChartPanningControls onClickPan={(delta) => handlePan(-delta)} />
          )}
          <DivAxisWrapper
            divAxisStyle={axisFontProps}
            ticks={bottomTicks.formattedTicks}
            label={bottomConfig.label}
            isTickVertical
            isTickRotated={isTickRotated}
          >
            <AxisBottom
              data-testid="axis-bottom"
              scale={bottomScale}
              containerSize={availableWidth}
              height={availableHeight}
              tickValues={bottomTicks.ticks}
              tickLabelProps={tickLabelProps}
              tickFormat={tickFormat}
              label={bottomConfig.label}
              tickComponent={(renderProps) => (
                <XTickWithTruncation
                  renderProps={renderProps}
                  maxSpaceForTruncation={getXTickMaxSize(
                    availableHeight,
                    availableWidth,
                    isTickRotated,
                    bottomScale,
                    bottomConfig.label,
                  )}
                  centerTick={isCategoricalAxis}
                  isTickRotated={isTickRotated}
                  position="bottom"
                />
              )}
            />
          </DivAxisWrapper>
        </div>
      )}
    </ChartGrid.GridArea>
  );
}

XYChartBottomAxisArea['displayName'] = 'XYChartValueBottomAxisArea';
